import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { EMPTY_VALUE, formatCurrency, formatDate } from "../../i18n/format";
import { isContractStatus } from "../../lib/contractStatus";
import type { Contract } from "../../services/contracts/generated/model";

import "./ContractDetailsModal.css";

interface ContractDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract | null;
  myCompanyId: number | null;
}

function statusClass(status: string): string {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "accepted" || s === "completed") return "contract-details__status--signed";
  if (s === "cancelled") return "contract-details__status--expired";
  return "contract-details__status--pending";
}

function formatValue(raw: string | null): string {
  if (raw == null || raw === "") return EMPTY_VALUE;
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  return formatCurrency(num);
}

const ContractDetailsModal = ({ isOpen, onClose, contract, myCompanyId }: ContractDetailsModalProps) => {
  const { t } = useTranslation("b2b");
  const contractStatusLabel = (status: string): string => {
    const s = (status || "").toLowerCase();
    return isContractStatus(s) ? t(`contracts.status.${s}`) : status;
  };
  if (!isOpen || !contract) return null;

  const aIsMine = myCompanyId != null && Number(contract.companyAId) === myCompanyId;
  const bIsMine = myCompanyId != null && Number(contract.companyBId) === myCompanyId;
  const counterpart = aIsMine ? contract.companyBName : contract.companyAName;
  const title = t("contractDetails.title", {
    type: contract.contractType || t("contractDetails.contractFallback"),
    counterpart: counterpart || EMPTY_VALUE,
  });

  return createPortal(
    <div className="contract-details__overlay" onClick={onClose}>
      <div
        className="contract-details__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contract-details-title"
      >
        <header className="contract-details__header">
          <h1 id="contract-details-title" className="contract-details__title">
            {title}
          </h1>
          <button
            type="button"
            className="contract-details__close"
            onClick={onClose}
            aria-label={t("contractDetails.closeAria")}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="contract-details__content">
          <p className="contract-details__company">{counterpart || "—"}</p>
          {contract.referenceId && (
            <p className="contract-details__reference">
              {t("contractDetails.reference", { ref: contract.referenceId })}
            </p>
          )}

          <div className="contract-details__grid">
            <div className="contract-details__field">
              <label className="contract-details__label">{t("contractDetails.fields.contractType")}</label>
              <p className="contract-details__value">{contract.contractType || "—"}</p>
            </div>

            <div className="contract-details__field">
              <label className="contract-details__label">{t("contractDetails.fields.contractValue")}</label>
              <p className="contract-details__value">{formatValue(contract.contractValue)}</p>
            </div>

            <div className="contract-details__field">
              <label className="contract-details__label">{t("contractDetails.fields.dateSigned")}</label>
              <p className="contract-details__value">{formatDate(contract.dateSigned)}</p>
            </div>

            <div className="contract-details__field">
              <label className="contract-details__label">{t("contractDetails.fields.status")}</label>
              <span className={`contract-details__status ${statusClass(contract.status)}`}>
                {contractStatusLabel(contract.status)}
              </span>
            </div>

            <div className="contract-details__field">
              <label className="contract-details__label">{t("contractDetails.fields.startDate")}</label>
              <p className="contract-details__value">{formatDate(contract.startDate)}</p>
            </div>

            <div className="contract-details__field">
              <label className="contract-details__label">{t("contractDetails.fields.endDate")}</label>
              <p className="contract-details__value">{formatDate(contract.endDate)}</p>
            </div>
          </div>

          <section className="contract-details__section">
            <h2 className="contract-details__section-title">{t("contractDetails.description")}</h2>
            <p className="contract-details__description">
              {contract.description || t("contractDetails.noDescription")}
            </p>
          </section>

          <section className="contract-details__section">
            <h2 className="contract-details__section-title">{t("contractDetails.terms")}</h2>
            <p className="contract-details__terms">{contract.termsAndConditions || t("contractDetails.noTerms")}</p>
          </section>

          {contract.documents.length > 0 && (
            <section className="contract-details__section">
              <h2 className="contract-details__section-title">{t("contractDetails.documents")}</h2>
              <ul className="contract-details__documents">
                {contract.documents.map((doc, i) => (
                  <li key={i}>
                    <a href={doc} target="_blank" rel="noopener noreferrer">
                      <i className="fa-regular fa-file-pdf" />
                      <span>{doc.split("/").pop() || t("contractDetails.documentFallback", { index: i + 1 })}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="contract-details__section">
            <h2 className="contract-details__section-title">{t("contractDetails.parties")}</h2>

            <div className="contract-details__party">
              <div
                className={`contract-details__party-icon ${aIsMine ? "contract-details__party-icon--green" : "contract-details__party-icon--blue"}`}
              >
                <i className="fa-solid fa-building" />
              </div>
              <div className="contract-details__party-info">
                <h3 className="contract-details__party-title">
                  {aIsMine ? t("contractDetails.partyAMine") : t("contractDetails.partyA")}
                </h3>
                <p className="contract-details__party-name">{contract.companyAName || "—"}</p>
              </div>
            </div>

            <div className="contract-details__party">
              <div
                className={`contract-details__party-icon ${bIsMine ? "contract-details__party-icon--green" : "contract-details__party-icon--blue"}`}
              >
                <i className="fa-solid fa-building" />
              </div>
              <div className="contract-details__party-info">
                <h3 className="contract-details__party-title">
                  {bIsMine ? t("contractDetails.partyBMine") : t("contractDetails.partyB")}
                </h3>
                <p className="contract-details__party-name">{contract.companyBName || "—"}</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ContractDetailsModal;
